import {
    ConstantsField,
    getOutputByType,
    InputsField,
    isConsequenceCreateObject,
    isConsequenceSetVariableField,
    OutputsField,
    ResponseWrapper,
    RulesListField,
    TestingForm,
} from '@innovatm/js-rule-editor';
import { Box, Button } from '@mui/material';
import React, { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { IRuleGeneric, RuleTestDto, useMutationPostTestRules } from '../../../backend';
import { isResourceManagerRule } from '../../../backend/utils';
import { useCurrentUserClient } from '../../../contexts/OidcContext/OidcContext.selectors';
import { useBoolean } from '../../../hooks/useBoolean';
import { ROUTE_EDITOR } from '../../../routes';
import { theme } from '../../../theme';
import { errorToastConfig, successToastConfig } from '../../../utils/utils';
import { RoundButton } from '../../Button/RoundButton/RoundButton';
import { ResourceRuleEditorTitle } from '../ResourceRuleEditorTitle';
import { StyledRuleEditorButtonsBox } from '../RuleEditor.style';
import {
    StyledDisplayInputsBox,
    StyledTestingConfigurationBox,
    StyledTestingEditorBox,
    StyledTestingInputsDataBox,
    StyledTestingSelectBox,
} from './TestingEditor.style';

const getOutputByTypeExtended = {
    ...getOutputByType,
    ResourceManagerRule: getOutputResourceRule,
};

export const TestingEditor = memo(function TestingEditor({ rules }: { rules: IRuleGeneric[] }) {
    const client = useCurrentUserClient();
    const { name } = useParams();
    const { value: displayInputs, setTrue, setFalse } = useBoolean(true);
    const navigate = useNavigate();
    const onCancel = useCallback(() => navigate(ROUTE_EDITOR), [navigate]);

    const {
        mutate: postTestRule,
        isLoading,
        data: responseTest,
        error: errorTest,
    } = useMutationPostTestRules({
        onSuccess: () => {
            toast(`The rule is successfully tested.`, successToastConfig);
        },
        onError: error => {
            Object.keys(error?.response?.data).map(errorKey =>
                toast(`${error?.response?.data[errorKey]}`, errorToastConfig),
            );
        },
    });
    const testRule = useCallback(
        (ruleTestDto: RuleTestDto) => {
            postTestRule({
                ruleTestDto,
                context: client.airportIata,
            });
        },
        [client.airportIata, postTestRule],
    );

    return (
        <div>
            <ResourceRuleEditorTitle
                title={<FormattedMessage id={'ruleEditor.testing.title'} />}
                tooltip={
                    <div>
                        <span>
                            This page allows you to test your rules with
                            <br />a controlled set of data.
                        </span>
                    </div>
                }
            />
            <TestingForm ruleSelected={name} onSubmit={testRule}>
                <StyledTestingEditorBox>
                    <StyledTestingConfigurationBox>
                        <StyledTestingSelectBox>
                            <Box width={'150px'} marginRight={'20px'}>
                                Rules to test :{' '}
                            </Box>
                            <RulesListField rules={rules as any} outputMap={getOutputByTypeExtended} />
                        </StyledTestingSelectBox>

                        <StyledTestingSelectBox>
                            <Box width={'150px'} marginRight={'20px'}>
                                Test outputs :{' '}
                            </Box>
                            <OutputsField />
                        </StyledTestingSelectBox>
                    </StyledTestingConfigurationBox>
                    <StyledTestingInputsDataBox>
                        <Box display={'flex'} alignItems={'center'} justifyContent={'space-around'} width={'350px'}>
                            <Box>Test input data : </Box>
                            <Button
                                variant={displayInputs ? 'contained' : 'outlined'}
                                onClick={setTrue}
                                sx={{ color: 'white', border: '1px solid #006EBB' }}
                            >
                                Inputs
                            </Button>
                            <Button
                                variant={displayInputs ? 'outlined' : 'contained'}
                                onClick={setFalse}
                                sx={{ color: 'white', border: '1px solid #006EBB' }}
                            >
                                Constants
                            </Button>
                        </Box>
                        <Box sx={{ width: '420px' }}></Box>
                    </StyledTestingInputsDataBox>
                    <StyledDisplayInputsBox>
                        {displayInputs ? <InputsField /> : <ConstantsField />}
                        <Box color={theme.palette.warning.dark}>
                            <FormattedMessage id={'ruleeditor.testing.flight.alert'} />
                        </Box>
                    </StyledDisplayInputsBox>
                    <Box sx={{ marginTop: '20px' }}>
                        {responseTest && !errorTest && <ResponseWrapper data={responseTest} />}
                    </Box>
                </StyledTestingEditorBox>
                <StyledRuleEditorButtonsBox>
                    <RoundButton color="primary" size="medium" variant="contained" loading={isLoading} type="submit">
                        <FormattedMessage id={'ruleEditor.editor.test'} />
                    </RoundButton>
                    &emsp;
                    <RoundButton color="primary" size="medium" variant="outlined" onClick={onCancel}>
                        <FormattedMessage id={'resourcesForm.cancel'} />
                    </RoundButton>
                </StyledRuleEditorButtonsBox>
            </TestingForm>
        </div>
    );
});

function getOutputResourceRule(rule: IRuleGeneric): string {
    let output = '';
    if (
        isResourceManagerRule(rule) &&
        rule.conditions?.conditionOnObjects &&
        isConsequenceSetVariableField(rule.consequence)
    ) {
        const variableNameConsq = rule.consequence.variable;
        rule.conditions.conditionOnObjects.forEach(element => {
            if (element.variableName === variableNameConsq) {
                output = element.objectType;
            }
        });
    } else if (
        isResourceManagerRule(rule) &&
        rule.conditions?.conditionOnObjects &&
        isConsequenceCreateObject(rule.consequence) &&
        rule.consequence.objectType
    ) {
        output = rule.consequence.objectType;
    }
    return output;
}
